import { useNavigate } from 'react-router-dom'
import { CheckCircle2, XCircle, Ban, Image, Package, RotateCcw, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { type TaskState } from '@/contexts/TaskManagerContext'
import { cn } from '@/lib/utils'
import { RelativeTime } from '@/components/ui/relative-time'
import { getTaskLabel } from '@/lib/task-labels'

interface TaskCompletedCardProps {
  task: TaskState
  /** Compact mode for dropdown panel */
  compact?: boolean
  /** Callback to dismiss this task from the list */
  onDismiss?: () => void
}

/**
 * Card showing a completed, failed, or cancelled task.
 * The entire card is clickable to navigate to the entity.
 * Failed tasks also show a Retry button.
 */
export function TaskCompletedCard({ task, compact = false, onDismiss }: TaskCompletedCardProps) {
  const navigate = useNavigate()

  const handleNavigate = () => {
    if (task.entityType === 'design') {
      navigate(`/designs/${task.entityId}`)
    } else if (task.entityType === 'batch') {
      navigate(`/batches/${task.entityId}`)
    }
  }

  const EntityIcon = task.entityType === 'batch' ? Package : Image

  const StatusIcon =
    task.status === 'completed'
      ? CheckCircle2
      : task.status === 'cancelled'
        ? Ban
        : XCircle

  const statusColor =
    task.status === 'completed'
      ? 'text-[hsl(var(--success))]'
      : task.status === 'cancelled'
        ? 'text-muted-foreground'
        : 'text-destructive'

  return (
    <div
      onClick={handleNavigate}
      className={cn(
        'cursor-pointer rounded-lg border bg-card transition-colors hover:bg-muted/50',
        compact ? 'p-2' : 'p-3'
      )}
    >
      <div className="flex items-center gap-3">
        {/* Status icon */}
        <div className="relative flex-shrink-0">
          <div
            className={cn(
              'flex items-center justify-center rounded-md bg-muted',
              compact ? 'h-8 w-8' : 'h-10 w-10'
            )}
          >
            <EntityIcon className={cn('text-muted-foreground', compact ? 'h-4 w-4' : 'h-5 w-5')} />
          </div>
          <div className="absolute -bottom-1 -right-1 rounded-full bg-card p-0.5">
            <StatusIcon className={cn('h-3.5 w-3.5', statusColor)} />
          </div>
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Badge
              variant={task.status === 'completed' ? 'success' : task.status === 'cancelled' ? 'secondary' : 'destructive'}
              className={cn('whitespace-nowrap', compact ? 'text-[10px]' : 'text-xs')}
            >
              {getTaskLabel(task.taskType, 'badge')}
            </Badge>
            <p className={cn('truncate font-medium', compact ? 'text-sm' : 'text-sm')}>
              {task.displayName ?? task.entityId.slice(0, 8)}
            </p>
          </div>
          <p className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-xs')}>
            {task.status === 'completed' ? 'Completed' : task.status === 'cancelled' ? 'Cancelled' : 'Failed'}
            {' \u2022 '}
            <RelativeTime date={task.completedAt} />
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {task.status === 'failed' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                handleNavigate()
              }}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              title="Retry this operation"
            >
              <RotateCcw className={cn('mr-1', compact ? 'h-3 w-3' : 'h-4 w-4')} />
              Retry
            </Button>
          )}
          {onDismiss && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation()
                onDismiss()
              }}
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              title="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
